import { datadogRum } from '@datadog/browser-rum';
import { TrackJS } from 'trackjs';

import type { ConfigOptions } from 'src/types';
import { getErrorMessage } from 'src/utils/logging';

export const bootstrap = (config: ConfigOptions) => {
  TrackJS.install({
    token: 'c0dee68c9f294d4d8011e8e45b0a74ec',
    application:
      config.environment === 'production'
        ? 'hirer-jobs-list-ui'
        : 'job-listing-ui-dev-stgn',
    enabled: config.environment !== 'development',
    console: {
      error: config.environment === 'production',
      watch: ['log', 'info', 'warn', 'error'], // Ignore 'debug' console events
    },
    onError(payload, error: Error | undefined) {
      const isCypress = Boolean(global.window.Cypress);
      const shouldSendToTrackJS = !isCypress;
      if (!shouldSendToTrackJS) {
        return false;
      }

      let message = getErrorMessage(error) || 'Unknown Error';
      let meta: any = {};
      try {
        const parsed = JSON.parse(error?.message || '{}');
        message = getErrorMessage(parsed) || message;
        meta = parsed.meta || meta;
      } catch (_ignore) {
        // Ignore any message that can't be JSON.parse'd - it likely isn't JSON and just a console.error
      }

      // Update Error Message
      payload.message = message;

      // Update Custom Meta Data
      Object.keys(meta).forEach((key: string) => {
        try {
          const rawValue = meta[key];
          // NOTE: For error value we only want the name and message (omit the stack trace)
          const value = JSON.stringify(
            key === 'error' && rawValue
              ? { name: rawValue.name, message: rawValue.message }
              : rawValue,
          );
          payload.metadata.push({ key, value });
        } catch (_ignore) {
          // Ignore any meta data that can't be JSON.stringify'd
        }
      });

      // Use error stacktrace if provided
      payload.stack = meta?.error?.stack || payload.stack;

      return true;
    },
  });

  datadogRum.init({
    applicationId: '77fc1642-2db8-4b6c-b4e3-43017876bd78',
    clientToken: 'pub28a0d77d4413deafb41fe7b2871b060a',
    site: 'datadoghq.com',
    service: 'jobs-list-ui',
    env: config.environment,
    version: config.version,
    allowedTracingUrls: [
      'https://talent.seek.com.au',
      'https://talent.seek.co.nz',
      'https://talent.seek.com.au.staging',
      'https://talent.seek.co.nz.staging',
      'https://talent.staging.seek.com.au',
      'https://talent.staging.seek.co.nz',
      // Asia Domains
      'https://ph.employer.staging.seek.com',
      'https://ph.employer.seek.comm',
      'https://th.employer.staging.seek.com',
      'https://th.employer.seek.com',
      'https://my.employer.staging.seek.com',
      'https://my.employer.seek.com',
      'https://hk.employer.staging.seek.com',
      'https://hk.employer.seek.com',
      'https://id.employer.staging.seek.com',
      'https://id.employer.seek.com',
      'https://sg.employer.staging.seek.com',
      'https://sg.employer.seek.com',
      /http:\/\/hirer-graphql-api-.*\.hirer\.dev\.outfra\.xyz/,
    ],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
};
